import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AnimatedCircleCounter from "../ui/AnimatedCircleCounter";
import CopyrightSymbol from "../ui/svg/CopyrightSymbol";
import FacebookIcon from "../ui/svg/FacebookIcon";
import TwitterIcon from "../ui/svg/TwitterIcon";
import LinkedInIcon from "../ui/svg/LinkedInIcon";
import InstagramIcon from "../ui/svg/InstagramIcon";
import { Link } from "react-router-dom";

const Footer = () => {
  const { i18n } = useTranslation();
  const { t } = useTranslation();
  const [imageSrc, setImageSrc] = useState("");
  const numbersData = [
    { label: t("Footer.footerdrones"), targetNumber: 4 },
    { label: t("Footer.footersensors"), targetNumber: 6 },
    { label: t("Footer.footercameras"), targetNumber: 8 },
    { label: t("Footer.footervehicles"), targetNumber: 2 },
    { label: t("Footer.footeroffroad"), targetNumber: 3 },
  ];
  const [isMobileView, setIsMobileView] = useState(window.innerWidth <= 1023);
  useEffect(() => {
    const handleResize = () => {
      setIsMobileView(window.innerWidth <= 1023);
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  useEffect(() => {
    const src = isMobileView
      ? i18n.language === "en"
        ? "/img/EN_fundedbyEU_VERTICAL_RGB_NEG.webp"
        : i18n.language === "mk"
        ? "/img/MK-funded_EU-emblem_NEG.webp"
        : i18n.language === "ks"
        ? "/img/KS-Funded_NEG.webp"
        : "/img/KS-Funded_NEG.webp"
      : i18n.language === "en"
      ? "/img/EN_FundedbytheEU_RGB_NEG.webp"
      : i18n.language === "mk"
      ? "/img/MK-funded_horizontal_EU-emblem_NEG.webp"
      : i18n.language === "ks"
      ? "/img/KS-Funded_EU-emblem_NEG.webp"
      : "/img/KS-Funded_EU-emblem_NEG.webp";

    setImageSrc(src);
  }, [i18n.language, isMobileView]);

  return (
    <footer className="bg-[#141212] text-white py-8">
      {/* First Section - Discover Us in Numbers */}
      <div className="container mx-auto px-4">
        <div className="flex flex-col items-center">
          <h2 className="text-[38px] font-semibold mb-10 py-2 xl:text-[38px] lg:text-[30px] xxs:text-center">
            {t("Footer.footertitle")}
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:gap-16 lg:flex lg:justify-center lg:gap-1 gap-16">
            {numbersData.map((item, index) => (
              <div
                key={index}
                className={`flex flex-col items-center ${
                  index === numbersData.length - 1
                    ? "md:col-span-2 md:justify-center"
                    : ""
                }`}
              >
                <p className="text-center text-white text-xl font-semibold mb-2 xl:text-xl lg:text-sm">
                  {item.label}
                </p>
                <AnimatedCircleCounter
                  targetNumber={item.targetNumber}
                  className="mb-2"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
      <hr className="border-t-1 border-white-900 my-10 mx-auto w-[85%]" />

      {/* Second Section - Contact & Footer Info */}
      <div className="px-18 mt-6 lg:px-[10%]">
        <div className="grid grid-cols-3 mb-2 xl:grid-cols-3 lg:grid-cols-3 md:w-full md:grid-cols-1 md:items-center sm:grid-cols-1 xs:items-center xxs:grid-cols-1 xxs:w-full xxs:items-center items-start gap-8">
          <div className="flex flex-col items-center">
            {/* Green bordered container with text */}
            <div className="w-64 h-[200px] xl:w-full xl:h-[250px] lg:w-[220px] lg:h-[232px] md:w-full md:h-full xxs:h-full flex flex-col items-center justify-center rounded-lg">
              {/* Image */}
              <img
                src={imageSrc}
                alt="Logo"
                className="h-32 w-42 mb-6 xl:mt-1 xl:h-auto xl:w-[500px] lg:h-auto lg:w-38 md:w-[320px] md:h-[320px] xxs:h-40"
              />
              <div className="text-justify mx-auto xl:text-left xl:w-full lg:text-justify lg:w-full md:text-center md:w-[380px] xxs:mx-1 xxs:text-center">
                {t("Footer.footerfundedtext")}
              </div>
            </div>
          </div>

          {/* Logo Section */}
          <div className="flex flex-col items-center lg:ml-4 xl:ml-8 2xl:ml-12 2xl:mb-6">
            <img
              src="/img/logo.webp"
              alt="Logo"
              className="lg:h-56 xxs:h-[150px] sm:h-64 xs:h-60 md:h-72"
            />
            <div className="text-center text-sm mt-2"></div>
          </div>

          {/* Placeholder for Social Media and Contact */}
          <div className="space-y-2 px-8 mb-1 text-right 2xl:w-full 2xl:relative xl:relative xl:w-full xl:mb-1 lg:text-right md:text-center md:w-full md:ml-0 xxs:ml-[0px] xxs:px-2 xxs:w-full xxs:h-full xxs:text-center mx-auto">
            <h3 className="text-3xl xl:text-3xl lg:text-3xl md:text-4xl font-semibold inline-block border-b-4 border-green-800 pb-1 pt-4">
              {t("Footer.footercontacttitle")}
            </h3>
            <br />
            <p className="inline-block text-lg xl:text-lg lg:text-xl md:text-xl border-b-2 border-green-800 pb-1 mt-4">
              {t("Footer.footercontactemail")}
            </p>
            <p>info@lepenecmonitoringsolutions.eu</p>
            <p className="inline-block text-lg xl:text-lg lg:text-xl md:text-xl border-b-2 border-green-800 pb-1 mt-4">
              {t("Footer.footercontactphone")}
            </p>
            <p>+38970222333</p>
          </div>
          {isMobileView && (
            <div className="flex justify-center items-center mb-6 md:w-full xxs:w-[100%]">
              <div className="w-5 h-5 flex-shrink-0 mr-1 mb-9 md:w-[50px] md:mr-[10px] md:ml-1 xxs:mr-[5px] xxs:mt-2 xxs:ml-[0px] xxs:w-[20px]">
                <svg
                  height="100%"
                  width="100%"
                  version="1.1"
                  id="Capa_1"
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 367.467 367.467"
                >
                  <defs>
                    <linearGradient
                      id="greenGradient"
                      x1="0%"
                      y1="0%"
                      x2="0%"
                      y2="100%"
                    >
                      <stop
                        offset="0%"
                        style={{ stopColor: "#004d00", stopOpacity: 1 }}
                      />
                      <stop
                        offset="100%"
                        style={{ stopColor: "#00e600", stopOpacity: 1 }}
                      />
                    </linearGradient>
                  </defs>
                  <g>
                    <path
                      fill="url(#greenGradient)"
                      d="M183.73,0.018C82.427,0.018,0,82.404,0,183.733c0,101.289,82.427,183.716,183.73,183.716
          c101.315,0,183.737-82.427,183.737-183.716C367.467,82.404,285.045,0.018,183.73,0.018z M183.73,326.518
          c-78.743,0-142.798-64.052-142.798-142.784c0-78.766,64.055-142.817,142.798-142.817c78.752,0,142.807,64.052,142.807,142.817
          C326.536,262.466,262.481,326.518,183.73,326.518z"
                    />
                    <path
                      fill="url(#greenGradient)"
                      d="M244.036,217.014c-11.737,20.141-33.562,32.635-56.956,32.635c-36.329,0-65.921-29.585-65.921-65.915
          c0-36.36,29.592-65.955,65.921-65.955c23.395,0,45.219,12.54,56.956,32.641l1.517,2.627h44.28l-2.658-7.129
          c-7.705-20.413-21.225-37.769-39.122-50.157c-17.942-12.42-39.017-19.009-60.973-19.009c-58.981,0-106.946,48.006-106.946,106.982
          c0,58.98,47.965,106.941,106.946,106.941c21.956,0,43.03-6.567,60.973-19.006c17.897-12.391,31.417-29.741,39.122-50.154
          l2.658-7.133h-44.28L244.036,217.014z"
                    />
                  </g>
                </svg>
              </div>

              {/* Text Content */}
              <div className="text-xs xxs:text-xs md:text-[1rem] flex flex-col justify-center items-center">
                {t("Footer.footercertified1")} <br />{" "}
                {t("Footer.footercertified2")} <br />
                {t("Footer.footercertified3")}
              </div>
            </div>
          )}
        </div>
        {/* Footer Bottom Text */}
        {!isMobileView && (
          <div className="flex flex-row w-full ml-0 xxs:space-y-4 xl:items-center md:flex-row md:items-center md:justify-between xs:items-center xxs:items-center">
            {/* Privacy Policy and Terms Text */}
            <div className="mb-0 2xl:mb-0 xl:mb-0 lg:mb-0 md:mb-0 sm:mb-24 xs:mb-24 xxs:mb-24">
              <a
                href="/privacy-policy"
                className="text-white-400 hover:text-green-300 lg:text-sm md:text-xs xxs:text-md xxs:text-justify xl:ml-[1px] md:ml-[0px] xxs:left-2"
              >
                {t("Footer.footerprivacy")}
              </a>
            </div>
            {/* Existing Text */}
            {!isMobileView && (
              <div className="flex items-center text-sm text-white-400 xl:w-[300px] xl:justify-center lg:w-[320px] xxs:w-[200px]">
                {/* SVG Logo */}
                <div className="w-5 h-5 flex-shrink-0 mr-2 mb-9 xxs:mr-[2px] xxs:w-[50px]">
                  <CopyrightSymbol />
                </div>

                {/* Text Content */}
                <div className="text-sm lg:text-[0.9rem]">
                  {t("Footer.footercertified1")} <br />{" "}
                  {t("Footer.footercertified2")} <br />
                  {t("Footer.footercertified3")}
                </div>
              </div>
            )}
            <div className="flex space-x-4 justify-end lg:mr-1 2xl:mr-1 xxs:justify-center">
              {/* Facebook Icon */}
              <Link
                to="https://www.facebook.com/people/A-Smart-System-for-Early-Floods-Detection-and-Waste/100066489858512/?locale=en_US&checkpoint_src=any"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer"
              >
                <FacebookIcon />
              </Link>

              {/* Twitter Icon */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <TwitterIcon startColor="#004d00" endColor="#00e600" />
              </div>

              {/* LinkedIn Icon */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <LinkedInIcon />
              </div>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <InstagramIcon />
              </div>
            </div>
          </div>
        )}
        {/* Footer Bottom Text For Mobile */}
        {isMobileView && (
          <div className="flex flex-col w-full xxs:space-y-4 xl:items-center md:px-10 md:flex-row md:items-center md:justify-between xs:items-center xxs:items-center">
            {/* Privacy Policy and Terms Text */}
            <div className="flex flex-row text-center items-center sm:mb-4 sm:mt-4 xs:mb-4 xs:mt-4 xxs:mb-4 xxs:mt-4 xxs:max-w-full">
              <a
                href="/privacy-policy"
                className="text-white-400 w-full mx-4 hover:text-green-300 lg:text-sm md:text-xs xxs:text-md xxs:text-center"
              >
                {t("Footer.footerprivacy")}
              </a>
            </div>
            {/* Existing Text */}
            {!isMobileView && (
              <div className="flex items-center text-sm text-white-400 2xl:ml-[-80px] xl:w-[300px] xl:ml-[-60px] xl:justify-center lg:ml-[-240px] lg:w-[320px] xxs:w-[200px]">
                {/* SVG Logo */}
                <div className="w-5 h-5 flex-shrink-0 mr-2 mb-9 xxs:mr-[2px] xxs:w-[50px]">
                  <CopyrightSymbol />
                </div>

                {/* Text Content */}
                <div className="text-sm lg:text-[0.9rem]">
                  {t("Footer.footercertified1")} <br />{" "}
                  {t("Footer.footercertified2")} <br />
                  {t("Footer.footercertified3")}
                </div>
              </div>
            )}
            <div className="flex flex-row space-x-4 justify-center items-center xxs:w-auto">
              {/* Facebook Icon */}
              <Link
                to="https://www.facebook.com/people/A-Smart-System-for-Early-Floods-Detection-and-Waste/100066489858512/?locale=en_US&checkpoint_src=any"
                target="_blank"
                rel="noopener noreferrer"
                className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer"
              >
                <FacebookIcon />
              </Link>

              {/* Twitter Icon */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <TwitterIcon />
              </div>

              {/* LinkedIn Icon */}
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <LinkedInIcon />
              </div>
              <div className="w-8 h-8 rounded-full flex items-center justify-center text-white cursor-pointer">
                <InstagramIcon />
              </div>
            </div>
          </div>
        )}
      </div>
    </footer>
  );
};

export default Footer;
